import { useEffect, useRef, useState } from "react";

interface RotatingWordProps {
  words: string[];
  /** How long a settled word is held before the next scramble, in ms. */
  holdMs?: number;
}

// Glyphs the scramble cycles through — monospace-ish shapes that read as
// "terminal noise" rather than as letters trying to be words.
const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ<>/\\[]{}#$%&*+=_01";

const TICK_MS = 45;
/** Ticks a character scrambles before it locks to its final letter. */
const TICKS_PER_CHAR = 3;

function randomGlyph() {
  return GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
}

/**
 * Cycles a word in place with a left-to-right character scramble: each letter
 * flickers through glyphs, then locks, so the word resolves like it's being
 * decoded. Falls back to the first word, static, under reduced motion.
 */
export function RotatingWord({ words, holdMs = 2200 }: RotatingWordProps) {
  const [display, setDisplay] = useState(words[0]);
  const indexRef = useRef(0);
  const frameRef = useRef<number | undefined>(undefined);
  const timeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setDisplay(words[0]);
      return;
    }

    let tick = 0;

    const scrambleTo = (target: string) => {
      const totalTicks = target.length * TICKS_PER_CHAR;

      const step = () => {
        tick += 1;
        const locked = Math.floor(tick / TICKS_PER_CHAR);
        const next = target
          .split("")
          .map((char, i) => (i < locked ? char : randomGlyph()))
          .join("");
        setDisplay(next);

        if (tick < totalTicks) {
          frameRef.current = window.setTimeout(step, TICK_MS);
        } else {
          setDisplay(target);
          timeoutRef.current = window.setTimeout(advance, holdMs);
        }
      };

      step();
    };

    const advance = () => {
      indexRef.current = (indexRef.current + 1) % words.length;
      tick = 0;
      scrambleTo(words[indexRef.current]);
    };

    timeoutRef.current = window.setTimeout(advance, holdMs);

    return () => {
      if (frameRef.current) window.clearTimeout(frameRef.current);
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [words, holdMs]);

  // Sized to the longest word so the line doesn't reflow mid-scramble.
  const widest = words.reduce((a, b) => (b.length > a.length ? b : a), "");

  return (
    <span
      style={{
        display: "inline-block",
        minWidth: `${widest.length}ch`,
        textAlign: "left",
        color: "var(--beam)",
      }}
      // The word changes on its own; announce the settled value, not every frame.
      aria-live="off"
    >
      {display}
    </span>
  );
}
